/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DXP_SERVICES } from '@squiz/dxp-auth';
import {
  API_TABLE_EVENT_PAGE_NEXT,
  API_TABLE_EVENT_PAGE_PREVIOUS,
  ApiTable,
  ApiTableFetchOptions,
  ApiTablePageTokens,
  ApiTableProps,
  EmptyState,
  EmptyStateProps,
  ICON_MISCELLANEOUS_SERVICES_WITH_LIST,
  SPINNER_TYPE_REGULAR,
  Spinner,
  TableCell,
  TableContent,
} from '@squiz/sds';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { CORE_STRINGS } from '../../Constants/Strings.constants';
import {
  EntityTenantSetting,
  EntityTenantSettings,
  TENANT_SETTINGS_PERMISSION,
  TenantSettingsServiceGetSettingsListDto,
  getTenantSettingsService,
} from '../../Services/tenant-settings';
import { hasPermissionForService } from '../../Utilities/HasPermissionForService/HasPermissionForService';
import { SETTING_LIST_DATA_IDS } from './SettingList.constants';
import { SettingListProps } from './SettingList.props';
import { SETTING_LIST_LINK_NEXT, SETTING_LIST_LINK_PREVIOUS } from './SettingList.types';
import { SettingCreate } from './elements/SettingCreate/SettingCreate';
import { SettingEdit } from './elements/SettingEdit/SettingEdit';
import { getPageToken } from './utils/GetPageToken/GetPageToken';
import { hasSettingCreatePermission } from './utils/HasSettingCreatePermission/HasSettingCreatePermission';

/**
 * View all service settings for a tenant.
 */
export const SettingList = (props: SettingListProps): JSX.Element => {
  const { t } = useTranslation();
  const { heading, keyName, schemaId, schemaList, serviceId } = props;

  // Tenant Settings service
  const tenantSettingsService = getTenantSettingsService();

  // State
  const [cacheKey, setCacheKey] = useState<string>('');
  const [settings, setSettings] = useState<EntityTenantSettings | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Configuration
  const apiTableRef = useRef(null);

  // Labels
  const labels = useMemo(() => {
    return {
      noSettings: t(CORE_STRINGS.noSettings),
      schema: t(CORE_STRINGS.schema),
      service: t(CORE_STRINGS.service),
      settingKey: t(CORE_STRINGS.settingKey),
    };
  }, [t]);

  // Permission
  let hasReadPermissionForList = true;
  if (serviceId) {
    hasReadPermissionForList = hasPermissionForService(TENANT_SETTINGS_PERMISSION.READ, serviceId);
  }

  // Page token object for pagination
  const pageTokens = useMemo(() => {
    const pageTokens: ApiTablePageTokens = {};

    if (settings && settings.links) {
      pageTokens.pageAfter = getPageToken(settings.links, SETTING_LIST_LINK_NEXT);
      pageTokens.pageBefore = getPageToken(settings.links, SETTING_LIST_LINK_PREVIOUS);
    }

    return pageTokens;
  }, [settings]);

  // Filtered schema options
  const schemaOptions = useMemo(() => {
    if (!schemaList) {
      return [];
    }

    if (schemaId) {
      return schemaList.data.filter((schema) => schema.schemaId === schemaId);
    }

    return schemaList.data;
  }, [schemaId, schemaList]);

  // Get new page of settings
  const getSettings = useCallback(
    (options: ApiTableFetchOptions) => {
      const { eventType, pageSize } = options;

      setIsLoading(true);

      let pageBefore: string | undefined;
      let pageAfter: string | undefined;

      if (eventType === API_TABLE_EVENT_PAGE_PREVIOUS) {
        pageBefore = pageTokens.pageBefore;
      }

      if (eventType === API_TABLE_EVENT_PAGE_NEXT) {
        pageAfter = pageTokens.pageAfter;
      }

      const dto: TenantSettingsServiceGetSettingsListDto = {
        pageAfter,
        pageBefore,
        pageSize,
      };

      if (serviceId) {
        dto.serviceId = serviceId;
      }

      if (schemaId) {
        dto.schemaId = schemaId;
      }

      lastValueFrom(tenantSettingsService.getSettingsList(dto))
        .then((response) => {
          setSettings(response as EntityTenantSettings);
          setIsLoading(false);
          return true;
        })
        .catch((e) => {
          // Just show regular empty message
          setSettings({ data: [] });
          setIsLoading(false);
        });
    },
    [pageTokens, schemaId, serviceId, setIsLoading, setSettings, tenantSettingsService],
  );

  let tableColumns: Array<TableCell> = [
    {
      content: keyName ?? labels.settingKey,
      key: `settingKey`,
    },
    {
      content: labels.service,
      key: `service`,
    },
    {
      content: labels.schema,
      key: `schema`,
    },
    {
      content: ``,
      key: `actions`,
    },
  ];

  // Remove the 'service/schema' column(s) if we have filtered the list
  if (serviceId) {
    tableColumns = tableColumns.filter((column) => column.key !== 'service');
  }

  if (schemaId) {
    tableColumns = tableColumns.filter((column) => column.key !== 'schema');
  }

  const tableRows = useMemo(() => {
    if (!settings || !schemaList) {
      return [];
    }

    return settings.data.map((setting: EntityTenantSetting) => {
      const { schemaId, serviceId, settingKey } = setting;

      return {
        actions: <SettingEdit clearCache={setCacheKey} keyName={keyName} schemas={schemaOptions} setting={setting} />,
        schema: schemaId,
        service: DXP_SERVICES[serviceId] || '-',
        settingKey,
      };
    });
  }, [keyName, schemaList, schemaOptions, settings]);

  // Reset settings state value when cacheKey is updated from an empty state (i.e. first setting is created)
  // This forces the ApiTable to render (and fetch the updated list) without reloading the page
  useEffect(() => {
    if (settings && settings.data.length === 0) {
      setSettings(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  if (!hasReadPermissionForList) {
    return <></>;
  }

  const createButton = (
    <SettingCreate clearCache={setCacheKey} keyName={keyName} schemas={schemaOptions} serviceId={serviceId} />
  );

  const noSettings =
    settings && schemaList && settings.data.length === 0 && !pageTokens.pageAfter && !pageTokens.pageBefore;
  if (noSettings) {
    const emptyStateClass = hasSettingCreatePermission() ? `sds-empty-state-card--with-cta` : ``;
    const emptyStateProps: EmptyStateProps = {
      caption: labels.noSettings,
      children: createButton,
      classes: emptyStateClass,
      dataId: SETTING_LIST_DATA_IDS.emptyState,
      svg: ICON_MISCELLANEOUS_SERVICES_WITH_LIST,
    };

    return (
      <div className="service-settings__wrapper">
        <EmptyState {...emptyStateProps} />
      </div>
    );
  }

  if (!schemaList) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  const tableContent: TableContent = { columns: tableColumns, rows: tableRows };
  const tableProps: ApiTableProps = {
    cacheKey,
    fetchData: getSettings,
    loading: isLoading,
    pageTokens,
    perPageDefault: 20,
    perPageOptions: [20, 50, 100],
    tableContent,
  };

  return (
    <div className="service-settings__wrapper">
      {heading && <h2>{heading}</h2>}
      {settings && schemaList && <div className="service-settings__actions">{createButton}</div>}
      {schemaList && <ApiTable {...tableProps} ref={apiTableRef} />}
    </div>
  );
};
